'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { SqlQueryExecution } from '@repo/api-gw-sdk';

import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { dayjs } from '@/utils/dayjs';

import { QueryExecutionStatusCell } from './queryExecutionStatusCell';

interface QueryExecutionPanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

// Helper to calculate duration
const formatDuration = (startDate: Date, endDate?: Date | null): string => {
  const end = endDate ? new Date(endDate) : new Date();
  const diff = end.getTime() - new Date(startDate).getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${hours}h ${minutes % 60}m`;
};

export const QueryExecutionPanel = ({
  id,
  workspaceId,
  onClose,
}: QueryExecutionPanelProps) => {
  const dal = useDAL();

  const { data: executionsData, isLoading: isExecutionsLoading } =
    dal.lakehouse.useListSqlQueryExecutions(workspaceId);

  const execution = executionsData?.sqlQueryExecutions?.find(
    (e: SqlQueryExecution) => e.id === id
  );

  if (isExecutionsLoading || !execution) {
    return (
      <PanelWrapper
        header={{
          title: 'Query Execution Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const isRunning =
    execution.status === 'RUNNING' || execution.status === 'QUEUED';

  return (
    <PanelWrapper
      header={{
        title: 'Query Execution Details',
        onClose,
      }}
    >
      <Stack gap='24px' padding='24px 40px'>
        {/* Execution Details */}
        <Stack gap='12px'>
          <DetailRow label='ID' value={execution.id} />
          <DetailRow
            label='Status'
            value={<QueryExecutionStatusCell status={execution.status} />}
          />
          <DetailRow
            label='SQL Engine'
            value={`${execution.sqlEngineName} (${execution.sqlEngineType})`}
          />
          <DetailRow
            label='Started'
            value={dayjs
              .utc(execution.createdAt)
              .format('MMM D, YYYY HH:mm:ss')}
          />
          {execution.finishedAt && (
            <DetailRow
              label='Finished'
              value={dayjs
                .utc(execution.finishedAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
          )}
          <DetailRow
            label='Duration'
            value={`${formatDuration(execution.createdAt, execution.finishedAt)}${isRunning ? '...' : ''}`}
          />
        </Stack>

        <Divider />

        {/* Query Text */}
        <Stack gap='8px'>
          <Typography variant='subtitle2' color='text.secondary'>
            Query
          </Typography>
          <Typography
            variant='body2'
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: 'var(--mui-palette-action-hover)',
              borderRadius: '4px',
              padding: '12px',
            }}
          >
            {execution.query}
          </Typography>
        </Stack>
      </Stack>
    </PanelWrapper>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
